const F2 = 0.5 * (Math.sqrt(3.0) - 1.0);
const G2 = (3.0 - Math.sqrt(3.0)) / 6.0;

/**
 * Seeded mulberry32 PRNG — returns values in [0, 1).
 * @param {number} seed - 32-bit integer seed
 * @returns {() => number} deterministic random number generator
 */
function mulberry32(seed) {
    return function () {
        seed |= 0;
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export default class SimplexNoise {

    /**
     * @param {number} [seed] - Optional seed for deterministic noise.
     *   When omitted, uses Math.random() (non-deterministic).
     */
    constructor(seed) {
        this.p = new Uint8Array(256);
        this.perm = new Uint8Array(512);
        this.permMod12 = new Uint8Array(512);
        this.grad3 = new Float32Array([
            1, 1, 0,
            -1, 1, 0,
            1, -1, 0,
            -1, -1, 0,
            1, 0, 1,
            -1, 0, 1,
            1, 0, -1,
            -1, 0, -1,
            0, 1, 1,
            0, -1, 1,
            0, 1, -1,
            0, -1, -1
        ]);

        const rng = seed != null ? mulberry32(seed) : Math.random;

        for (let i = 0; i < 256; i++) {
            this.p[i] = rng() * 256;
        }
        for (let i = 0; i < 512; i++) {
            this.perm[i] = this.p[i & 255];
            this.permMod12[i] = this.perm[i] % 12;
        }
    }

    noise2D(xin, yin) {
        let n0 = 0;
        let n1 = 0;
        let n2 = 0;

        const s = (xin + yin) * F2;
        const i = Math.floor(xin + s);
        const j = Math.floor(yin + s);
        const t = (i + j) * G2;

        const xx0 = i - t;
        const yy0 = j - t;

        const x0 = xin - xx0;
        const y0 = yin - yy0;

        const i1 = x0 > y0 ? 1 : 0;
        const j1 = (i1 + 1) % 2;

        const x1 = x0 - i1 + G2;
        const y1 = y0 - j1 + G2;
        const x2 = x0 - 1 + 2 * G2;
        const y2 = y0 - 1 + 2 * G2;

        const ii = i & 255;
        const jj = j & 255;

        let t0 = 0.5 - x0 * x0 - y0 * y0;

        if (t0 >= 0) {
            const gi = this.permMod12[ii + this.perm[jj]] * 3;
            t0 *= t0;
            n0 = t0 * t0 * (this.grad3[gi] * x0 + this.grad3[gi + 1] * y0);
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 >= 0) {
            const gi = this.permMod12[ii + i1 + this.perm[jj + j1]] * 3;
            t1 *= t1;
            n1 = t1 * t1 * (this.grad3[gi] * x1 + this.grad3[gi + 1] * y1);
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 >= 0) {
            const gi = this.permMod12[ii + 1 + this.perm[jj + 1]] * 3;
            t2 *= t2;
            n2 = t2 * t2 * (this.grad3[gi] * x2 + this.grad3[gi + 1] * y2);
        }

        return 70.0 * (n0 + n1 + n2);
    }
}
